import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridRenderEditCellParams,
  GridRowEditStopReasons,
  GridRowModesModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { MdsIcon } from "@sinch/components";
import { CustomAttributeInputBase } from "@sinch/components/application/CustomAttributeInput/CustomAttributeInputBase";
import { PendingProfileChangeSchemaFormFields } from "@sinch/components/application/ProfileReviewDialog/formSchema";
import { isAttribute } from "@sinch/components/application/ProfileReviewDialog/tools";
import { AttributeValue } from "@sinch/components/application/Worker/Attributes/AttributeValue";
import { DateInput } from "@sinch/components/form/DateInput/DateInput";
import { GridActionsCellItem } from "@sinch/components/GridActionsCellItem/GridActionsCellItem";
import { AttributeTypeEnum } from "@sinch/core/entities/serverEnums";
import { MdsEdit } from "@sinch/icons";
import { equals } from "ramda";
import { FC, useMemo } from "react";
import { get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { WorkerMandatoryAttributesQuery } from "../queries.types";
import { match } from "ts-pattern";
import { ensureArray, isNotNilOrEmpty } from "ramda-adjunct";
import { useFormat } from "@sinch/core/hooks/format";

type MandatoryAttribute = WorkerMandatoryAttributesQuery["workerMandatoryAttribute"][number];
type MandatoryAttributeRow = MandatoryAttribute & { id: string | number };
type ApiRef = ReturnType<typeof useGridApiRef>;
type ChangeEventLike = { target?: { value: unknown } };

const getAttributeMeta = (attribute: MandatoryAttribute) => ({
  dataType: (isAttribute(attribute) ? attribute.attribute.dataType : attribute.dataType) as AttributeTypeEnum,
  options: (isAttribute(attribute) ? attribute.attribute.options : attribute.options) ?? undefined,
  files: attribute.files ?? undefined,
  attributeId: isAttribute(attribute) ? attribute.id : `system/${attribute.field}`,
  name: isAttribute(attribute) ? attribute.attribute.name : attribute.name,
});

const RowEditActionsCell: FC<GridRenderEditCellParams<MandatoryAttributeRow>> = ({ id, api }) => {
  const { t } = useTranslation();
  const {
    setValue,
    getValues,
    formState: { defaultValues },
  } = useFormContext<PendingProfileChangeSchemaFormFields>();

  const valueFieldName = `attributes.${+id}.value` as const;
  const expirationFieldName = `attributes.${+id}.expirationDate` as const;
  const values = getValues();
  const isRowDirty =
    !equals(get(defaultValues, valueFieldName), get(values, valueFieldName)) ||
    !equals(get(defaultValues, expirationFieldName), get(values, expirationFieldName));

  const handleApply = () => api.stopRowEditMode({ id });
  const handleCancel = () => api.stopRowEditMode({ id, ignoreModifications: true });
  const handleRevert = () => {
    const defaultValue = get(defaultValues, valueFieldName);
    const defaultExpiration = get(defaultValues, expirationFieldName);
    // resetField() is a no-op for these fields: they are never registered as
    // inputs (only written via setValue on row edit commit), so the form kept
    // the edited value and the approval still shipped it as an admin edit.
    // Write the defaults back explicitly instead.
    setValue(valueFieldName, defaultValue);
    setValue(expirationFieldName, defaultExpiration);

    api.updateRows([{ id: id, value: defaultValue, expirationDate: defaultExpiration, decision: null }]);
    api.stopRowEditMode({ id, ignoreModifications: true });
  };

  return (
    <>
      <Button color="info" data-cy="row-edit-apply-btn" onClick={handleApply} size="small">
        {t("apply")}
      </Button>
      {isRowDirty && (
        <Button color="warning" onClick={handleRevert} size="small">
          {t("revert")}
        </Button>
      )}
      <Button color="error" onClick={handleCancel} size="small">
        {t("cancel")}
      </Button>
    </>
  );
};

const ValueDisplayCell: FC<{ row: MandatoryAttributeRow; value: unknown }> = ({ row, value }) => {
  const { dataType, options, files, attributeId } = getAttributeMeta(row);
  // For file-backed attributes AttributeValue renders from `files`, not `value`.
  // An inline edit only updates `value` (carrying the freshly uploaded
  // {id,url,name}), so derive the preview from `value` to reflect the edit
  // instead of the worker's stale original `files`.
  const isFileType =
    dataType === AttributeTypeEnum.File ||
    dataType === AttributeTypeEnum.Image ||
    dataType === AttributeTypeEnum.Gallery;
  const resolvedFiles = isFileType
    ? ensureArray(value).filter(
        (file): file is { id: string; name: string; url: string } =>
          file !== null && typeof file === "object" && "url" in file
      )
    : files;
  return (
    <ValueDisplayWrapper data-cy="mandatory-attribute-value" data-field-key={attributeId}>
      <AttributeValue files={resolvedFiles} options={options} type={dataType} value={value} />
    </ValueDisplayWrapper>
  );
};

const ValueEditCell: FC<GridRenderEditCellParams<MandatoryAttributeRow>> = ({ id, row, value, field, api }) => {
  const { t } = useTranslation();
  const { dataType, options, attributeId } = getAttributeMeta(row);
  const handleValueChange = (val: ChangeEventLike | string | unknown[] | null) => {
    api.setEditCellValue({
      id,
      field,
      value: (val as ChangeEventLike)?.target?.value ?? val,
    });
  };
  return (
    <ValueEditWrapper data-cy="mandatory-attribute-value-edit" data-field-key={attributeId}>
      <CustomAttributeInputBase
        attribute={{ id: attributeId, dataType, options }}
        disablePortal={false}
        onChange={handleValueChange}
        placeholder={t("ProfileReview.enterCorrectValue")}
        previewSize="small"
        // Bind to the live edit value (params.value), not the static row.value, so the
        // upload widget's async completion updates the newly uploaded file instead of
        // re-subscribing the original and reverting the edit on commit.
        value={value}
      />
    </ValueEditWrapper>
  );
};

const DateEditCell: FC<GridRenderEditCellParams<MandatoryAttributeRow>> = ({ id, value, field, api }) => (
  <DateEditWrapper>
    <DateInput
      disablePortal
      onChange={(val: Date | null) => api.setEditCellValue({ id, field, value: val })}
      value={value}
    />
  </DateEditWrapper>
);

const useColumns = (apiRef: ApiRef, showExpiration: boolean): GridColDef<MandatoryAttributeRow>[] => {
  const { t } = useTranslation();
  const { date } = useFormat();

  return useMemo(
    () => [
      {
        field: "attributeName",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
        editable: false,
        valueGetter: (_, row) => getAttributeMeta(row).name,
      },
      {
        field: "value",
        headerName: "Value",
        type: "custom",
        flex: 1,
        editable: true,
        renderCell: ({ value, row }) => <ValueDisplayCell row={row} value={value} />,
        renderEditCell: (params) => <ValueEditCell {...params} />,
      },
      // The whole column is dropped when no listed attribute supports expiration -
      // it would render a wide column of dashes while long attribute names wrap.
      ...(showExpiration
        ? [
            {
              field: "expirationDate",
              headerName: "Expiration date",
              type: "date",
              width: 150,
              align: "left",
              headerAlign: "left",
              editable: true,
              renderCell: ({ row }) =>
                isAttribute(row) && isNotNilOrEmpty(row.expirationDate)
                  ? date.mediumMonthYear(row.expirationDate)
                  : "-",
              renderEditCell: (params) => <DateEditCell {...params} />,
            } satisfies GridColDef<MandatoryAttributeRow>,
          ]
        : []),
      {
        resizable: false,
        field: "actions",
        type: "actions",
        width: 200,
        display: "flex",
        maxWidth: 200,
        editable: true,
        headerAlign: "right",
        align: "right",
        renderEditCell: (props) => <RowEditActionsCell {...props} />,
        getActions: ({ id, row }) =>
          (isAttribute(row) ? true : row.editable)
            ? [
                <GridActionsCellItem
                  key="edit"
                  color="info"
                  data-cy="row-edit-start-btn"
                  icon={<MdsIcon icon={MdsEdit} />}
                  label={t("edit")}
                  onClick={() => apiRef.current?.startRowEditMode({ id })}
                />,
              ]
            : [],
      },
    ],
    [apiRef, t, date, showExpiration]
  );
};

const handleRowEditStop: NonNullable<DataGridProProps["onRowEditStop"]> = (params, event) => {
  if (params.reason === GridRowEditStopReasons.rowFocusOut) {
    event.defaultMuiPrevented = true;
  }
};

const getRowHeight = () => "auto" as const;
const isCellEditable: DataGridProProps<MandatoryAttributeRow>["isCellEditable"] = ({ row, field }) =>
  match(field)
    .with("value", () => (isAttribute(row) ? true : row.editable))
    .with("expirationDate", () => (isAttribute(row) ? row.attribute.expirable : false))
    .otherwise(() => true);

const dataGridSlots = { footer: () => null };

interface MandatoryAttributesTableProps {
  attributes: MandatoryAttribute[];
  onEditingChange: (editing: boolean) => void;
}

export const MandatoryAttributesTable: FC<MandatoryAttributesTableProps> = ({ attributes, onEditingChange }) => {
  const form = useFormContext<PendingProfileChangeSchemaFormFields>();
  const apiRef = useGridApiRef();
  const showExpiration = useMemo(
    () => attributes.some((attribute) => isAttribute(attribute) && attribute.attribute.expirable),
    [attributes]
  );
  const columns = useColumns(apiRef, showExpiration);

  const rows = useMemo<MandatoryAttributeRow[]>(
    () => attributes.map((attribute, index) => ({ ...attribute, id: String(index) })),
    [attributes]
  );

  const handleRowModesModelChange = (newModel: GridRowModesModel) => {
    onEditingChange(Object.keys(newModel).length > 0);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <MandatoryAttributesDataGrid<MandatoryAttributeRow>
        apiRef={apiRef}
        columns={columns}
        disableColumnFilter
        disableColumnMenu
        disableColumnPinning
        disableColumnReorder
        disableColumnResize
        disableColumnSorting
        disableVirtualization
        editMode="row"
        getRowHeight={getRowHeight}
        isCellEditable={isCellEditable}
        onRowEditStop={handleRowEditStop}
        onRowModesModelChange={handleRowModesModelChange}
        processRowUpdate={(updatedRow) => {
          form.setValue(`attributes.${+updatedRow.id}.value`, updatedRow.value);
          return updatedRow;
        }}
        rows={rows}
        slots={dataGridSlots}
      />
    </Box>
  );
};

const ValueDisplayWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const ValueEditWrapper = styled(Box)({
  width: "100%",
  "&>.MuiFormControl-root": { position: "initial" },
});

const DateEditWrapper = styled(Box)({
  width: "100%",
  "&>*": { position: "initial" },
});

const MandatoryAttributesDataGrid = styled(DataGridPro)({
  width: "100%",
  "& .MuiDataGrid-cell.MuiDataGrid-cell--textLeft": { paddingRight: "1px" },
  "& .MuiDataGrid-row": { minHeight: "44px !important" },
  "& .MuiDataGrid-cell.MuiDataGrid-cell--editing": {
    boxShadow: "none",
    "&:focus-within": { outline: "none" },
  },
  "& .MuiDataGrid-row--editing": { boxShadow: "none" },
}) as typeof DataGridPro;
